import React from 'react';
import { cn } from '../../utils/cn.ts';
import { GlassClear } from './GlassSurface/index.ts';

interface FloatingToolbarProps {
    children: React.ReactNode;
    className?: string;
}

const Divider: React.FC = () => (
    <div className="mx-1 h-6 w-px bg-gray-300 dark:bg-gray-600" />
);

type FloatingToolbarChildProps = {
    variant?: string | null;
    className?: string;
    children?: React.ReactNode;
};

type DisplayNamedComponent = {
    displayName?: string;
};

function hasDisplayName(type: unknown, displayName: string): boolean {
    if (typeof type !== 'function' && typeof type !== 'object') {
        return false;
    }

    return type !== null && 'displayName' in type && (type as DisplayNamedComponent).displayName === displayName;
}

export const FloatingToolbar: React.FC<FloatingToolbarProps> & { Divider: typeof Divider } = ({ 
    children, 
    className = '' 
}) => {
    // Helper to enhance a single element with pill styling
    const enhanceButton = (element: React.ReactElement<FloatingToolbarChildProps>): React.ReactElement => {
        const { variant = 'primary', className: childClassName } = element.props;
        
        // Determine shadow color based on variant
        let shadowClass = 'shadow-primary/30';
        if (variant === 'purple') shadowClass = 'shadow-purple-500/30';
        else if (variant === 'blue') shadowClass = 'shadow-blue-500/30';
        else if (variant === 'danger') shadowClass = 'shadow-red-500/30';
        else if (variant === 'secondary') shadowClass = 'shadow-gray-400/20';
        
        return React.cloneElement(element, {
            className: cn(
                'min-h-[44px] rounded-full! px-4! py-2.5! shadow-lg',
                shadowClass,
                childClassName
            )
        });
    };

    // Recursively enhance children, handling wrapper components
    const enhanceChildren = (nodes: React.ReactNode): React.ReactNode => {
        return React.Children.map(nodes, (child) => {
            if (!React.isValidElement<FloatingToolbarChildProps>(child)) return child;
            
            // Skip dividers
            if (child.type === Divider) return child;
            
            const childProps = child.props;
            
            // Check if this is a Button (has variant prop) - these get pill styling
            const isButton = childProps?.variant !== undefined || 
                             hasDisplayName(child.type, 'Button');
            
            if (isButton) {
                return enhanceButton(child);
            }
            
            // If it's a Fragment, just process its children
            if (child.type === React.Fragment) {
                return <>{enhanceChildren(childProps.children)}</>;
            }
            
            // For wrapper components with children (like PermissionGuard),
            // clone with enhanced children
            if (childProps?.children) {
                return React.cloneElement(child, {
                    children: enhanceChildren(childProps.children)
                });
            }
            
            // For other leaf elements, return as-is
            return child;
        });
    };

    const enhancedChildren = enhanceChildren(children);

    return (
        <div className={cn(
            'pointer-events-none fixed right-0 bottom-6 left-0 z-30 flex items-center justify-center gap-2 print:hidden',
            className
        )}>
            <GlassClear
                dimmed
                className={cn(
                    'flex items-center gap-1 p-1.5',
                    'rounded-full!',
                    'shadow-2xl shadow-black/10 dark:shadow-black/40',
                    'pointer-events-auto text-sm',
                    'ring-1 ring-black/5 dark:ring-white/10'
                )}
            >
                {enhancedChildren}
            </GlassClear>
        </div>
    );
};

FloatingToolbar.Divider = Divider;














